import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Mail, Phone, User, MessageSquare, CheckCircle, Trash2, X } from "lucide-react";

const PasswordResetRequestsPanel = ({ onClose }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/password-reset-requests");
      setRequests(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des demandes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markAsResolved = async (id) => {
    try {
      await api.put(`/admin/password-reset-requests/${id}`, null, { params: { status: "resolved" } });
      toast.success("Demande marquée comme résolue.");
      load();
    } catch {
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      await api.delete(`/admin/password-reset-requests/${id}`);
      toast.success("Demande supprimée.");
      load();
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-[#007EAE] text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Demandes de récupération de mot de passe</h2>
          <button onClick={onClose} className="text-white/90 hover:text-white p-1">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Chargement...</div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Aucune demande de récupération pour le moment.
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((req) => (
                <div
                  key={req.id}
                  className={`border p-5 ${req.status === "resolved" ? "bg-slate-50 border-slate-300" : "bg-white border-slate-200"}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-[#007EAE]" />
                      <h3 className="font-semibold text-slate-800">
                        {req.first_name} {req.last_name}
                      </h3>
                      {req.status === "resolved" && (
                        <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 border border-emerald-300">
                          Résolu
                        </span>
                      )}
                      {req.status === "pending" && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 border border-amber-300">
                          En attente
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">{formatDate(req.created_at)}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${req.email}`} className="text-[#007EAE] hover:underline">
                        {req.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${req.phone}`} className="text-[#007EAE] hover:underline">
                        {req.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 mb-3">
                    <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <div className="text-sm text-slate-700">{req.message}</div>
                  </div>

                  <div className="flex gap-2">
                    {req.status === "pending" && (
                      <button
                        onClick={() => markAsResolved(req.id)}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Marquer comme résolu
                      </button>
                    )}
                    <button
                      onClick={() => deleteRequest(req.id)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetRequestsPanel;
